
import { useRef } from 'react';

interface EditorToolbarProps {
  execCommand: (command: string, value?: string) => void;
  insertHtmlAtCursor: (html: string) => void;
  onFileUpload: (file: File) => void;
  contentUploading: boolean;
  editorRef: React.RefObject<HTMLDivElement>;
}

export default function EditorToolbar({
  execCommand,
  insertHtmlAtCursor,
  onFileUpload,
  contentUploading,
  editorRef,
}: EditorToolbarProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileUpload(file);
      e.target.value = '';
    }
  };

  // 見出しを挿入
  const insertHeading = (level: 2 | 3 | 4) => {
    const selection = window.getSelection();
    const selectedText = selection?.toString() || '見出しテキスト';
    const headingHtml = `<h${level}>${selectedText}</h${level}>`;
    insertHtmlAtCursor(headingHtml);
  };

  // テンプレートを挿入
  const insertTemplate = (type: string) => {
    let html = '';
    switch (type) {
      case 'important':
        html = `
          <div class="my-6 p-5 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg">
            <div class="flex items-start gap-3">
              <div class="w-6 h-6 flex items-center justify-center flex-shrink-0 text-amber-500">
                <i class="ri-error-warning-line text-xl"></i>
              </div>
              <div class="flex-1">
                <p class="font-bold text-amber-900 mb-2">重要ポイント</p>
                <p class="text-amber-800 leading-relaxed">ここに重要な情報を入力してください。</p>
              </div>
            </div>
          </div>
        `;
        break;
      case 'warning':
        html = `
          <div class="my-6 p-5 bg-red-50 border-l-4 border-red-500 rounded-r-lg">
            <div class="flex items-start gap-3">
              <div class="w-6 h-6 flex items-center justify-center flex-shrink-0 text-red-500">
                <i class="ri-alert-line text-xl"></i>
              </div>
              <div class="flex-1">
                <p class="font-bold text-red-900 mb-2">注意事項</p>
                <p class="text-red-800 leading-relaxed">ここに注意すべき内容を入力してください。</p>
              </div>
            </div>
          </div>
        `;
        break;
      case 'tips':
        html = `
          <div class="my-6 p-5 bg-teal-50 border-l-4 border-teal-500 rounded-r-lg">
            <div class="flex items-start gap-3">
              <div class="w-6 h-6 flex items-center justify-center flex-shrink-0 text-teal-500">
                <i class="ri-lightbulb-line text-xl"></i>
              </div>
              <div class="flex-1">
                <p class="font-bold text-teal-900 mb-2">プロのTips</p>
                <p class="text-teal-800 leading-relaxed">ここに役立つヒントを入力してください。</p>
              </div>
            </div>
          </div>
        `;
        break;
      case 'checklist':
        html = `
          <div class="my-6 p-5 bg-blue-50 border border-blue-200 rounded-lg">
            <div class="flex items-center gap-2 mb-4">
              <div class="w-6 h-6 flex items-center justify-center text-blue-500">
                <i class="ri-checkbox-line text-xl"></i>
              </div>
              <p class="font-bold text-blue-900">チェックリスト</p>
            </div>
            <ul class="space-y-2">
              <li class="flex items-start gap-2">
                <i class="ri-checkbox-blank-circle-line text-blue-500 mt-1 flex-shrink-0"></i>
                <span class="text-blue-800">項目1</span>
              </li>
              <li class="flex items-start gap-2">
                <i class="ri-checkbox-blank-circle-line text-blue-500 mt-1 flex-shrink-0"></i>
                <span class="text-blue-800">項目2</span>
              </li>
              <li class="flex items-start gap-2">
                <i class="ri-checkbox-blank-circle-line text-blue-500 mt-1 flex-shrink-0"></i>
                <span class="text-blue-800">項目3</span>
              </li>
            </ul>
          </div>
        `;
        break;
      case 'steps':
        html = `
          <div class="my-6 p-5 bg-purple-50 border border-purple-200 rounded-lg">
            <div class="flex items-center gap-2 mb-4">
              <div class="w-6 h-6 flex items-center justify-center text-purple-500">
                <i class="ri-list-ordered text-xl"></i>
              </div>
              <p class="font-bold text-purple-900">ステップバイステップ</p>
            </div>
            <div class="space-y-4">
              <div class="flex gap-3">
                <div class="w-8 h-8 flex items-center justify-center flex-shrink-0 bg-purple-500 text-white rounded-full font-bold text-sm">1</div>
                <div class="flex-1 pt-1">
                  <p class="font-semibold text-purple-900 mb-1">ステップ1のタイトル</p>
                  <p class="text-purple-800 text-sm">ステップ1の説明文</p>
                </div>
              </div>
              <div class="flex gap-3">
                <div class="w-8 h-8 flex items-center justify-center flex-shrink-0 bg-purple-500 text-white rounded-full font-bold text-sm">2</div>
                <div class="flex-1 pt-1">
                  <p class="font-semibold text-purple-900 mb-1">ステップ2のタイトル</p>
                  <p class="text-purple-800 text-sm">ステップ2の説明文</p>
                </div>
              </div>
              <div class="flex gap-3">
                <div class="w-8 h-8 flex items-center justify-center flex-shrink-0 bg-purple-500 text-white rounded-full font-bold text-sm">3</div>
                <div class="flex-1 pt-1">
                  <p class="font-semibold text-purple-900 mb-1">ステップ3のタイトル</p>
                  <p class="text-purple-800 text-sm">ステップ3の説明文</p>
                </div>
              </div>
            </div>
          </div>
        `;
        break;
    }
    insertHtmlAtCursor(html);
  };

  return (
    <div className="bg-gray-50 border-b border-gray-300 p-3">
      <div className="flex flex-wrap gap-2">
        {/* 見出しボタン */}
        <div className="flex gap-1 border-r border-gray-300 pr-2">
          <button
            type="button"
            onClick={() => insertHeading(2)}
            className="px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors text-sm font-semibold text-gray-700 cursor-pointer whitespace-nowrap"
            title="見出し2を挿入"
          >
            H2
          </button>
          <button
            type="button"
            onClick={() => insertHeading(3)}
            className="px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors text-sm font-semibold text-gray-700 cursor-pointer whitespace-nowrap"
            title="見出し3を挿入"
          >
            H3
          </button>
          <button
            type="button"
            onClick={() => insertHeading(4)}
            className="px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors text-sm font-semibold text-gray-700 cursor-pointer whitespace-nowrap"
            title="見出し4を挿入"
          >
            H4
          </button>
        </div>

        {/* テキスト装飾 */}
        <div className="flex gap-1 border-r border-gray-300 pr-2">
          <button
            type="button"
            onClick={() => execCommand('bold')}
            className="w-8 h-8 flex items-center justify-center bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors cursor-pointer"
            title="太字"
          >
            <i className="ri-bold"></i>
          </button>
          <button
            type="button"
            onClick={() => execCommand('italic')}
            className="w-8 h-8 flex items-center justify-center bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors cursor-pointer"
            title="斜体"
          >
            <i className="ri-italic"></i>
          </button>
          <button
            type="button"
            onClick={() => execCommand('underline')}
            className="w-8 h-8 flex items-center justify-center bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors cursor-pointer"
            title="下線"
          >
            <i className="ri-underline"></i>
          </button>
          <button
            type="button"
            onClick={() => execCommand('strikeThrough')}
            className="w-8 h-8 flex items-center justify-center bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors cursor-pointer"
            title="取り消し線"
          >
            <i className="ri-strikethrough"></i>
          </button>
        </div>

        {/* リスト */}
        <div className="flex gap-1 border-r border-gray-300 pr-2">
          <button
            type="button"
            onClick={() => execCommand('insertUnorderedList')}
            className="w-8 h-8 flex items-center justify-center bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors cursor-pointer"
            title="箇条書きリスト"
          >
            <i className="ri-list-unordered"></i>
          </button>
          <button
            type="button"
            onClick={() => execCommand('insertOrderedList')}
            className="w-8 h-8 flex items-center justify-center bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors cursor-pointer"
            title="番号付きリスト"
          >
            <i className="ri-list-ordered"></i>
          </button>
        </div>

        {/* リンク・画像 */}
        <div className="flex gap-1 border-r border-gray-300 pr-2">
          <button
            type="button"
            onClick={() => {
              const url = prompt('リンクURLを入力してください:');
              if (url) execCommand('createLink', url);
            }}
            className="w-8 h-8 flex items-center justify-center bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors cursor-pointer"
            title="リンク"
          >
            <i className="ri-link"></i>
          </button>
          <button
            type="button"
            onClick={handleImageClick}
            disabled={contentUploading}
            className="w-8 h-8 flex items-center justify-center bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            title="画像"
          >
            {contentUploading ? (
              <i className="ri-loader-4-line animate-spin"></i>
            ) : (
              <i className="ri-image-line"></i>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>

        {/* テンプレート */}
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => insertTemplate('important')}
            className="px-3 py-1.5 bg-amber-50 border border-amber-300 rounded hover:bg-amber-100 transition-colors text-xs text-amber-700 cursor-pointer whitespace-nowrap"
            title="重要ポイント"
          >
            <i className="ri-error-warning-line mr-1"></i>
            重要
          </button>
          <button
            type="button"
            onClick={() => insertTemplate('warning')}
            className="px-3 py-1.5 bg-red-50 border border-red-300 rounded hover:bg-red-100 transition-colors text-xs text-red-700 cursor-pointer whitespace-nowrap"
            title="注意事項"
          >
            <i className="ri-alert-line mr-1"></i>
            注意
          </button>
          <button
            type="button"
            onClick={() => insertTemplate('tips')}
            className="px-3 py-1.5 bg-teal-50 border border-teal-300 rounded hover:bg-teal-100 transition-colors text-xs text-teal-700 cursor-pointer whitespace-nowrap"
            title="プロのTips"
          >
            <i className="ri-lightbulb-line mr-1"></i>
            Tips
          </button>
          <button
            type="button"
            onClick={() => insertTemplate('checklist')}
            className="px-3 py-1.5 bg-blue-50 border border-blue-300 rounded hover:bg-blue-100 transition-colors text-xs text-blue-700 cursor-pointer whitespace-nowrap"
            title="チェックリスト"
          >
            <i className="ri-checkbox-line mr-1"></i>
            リスト
          </button>
          <button
            type="button"
            onClick={() => insertTemplate('steps')}
            className="px-3 py-1.5 bg-purple-50 border border-purple-300 rounded hover:bg-purple-100 transition-colors text-xs text-purple-700 cursor-pointer whitespace-nowrap"
            title="ステップバイステップ"
          >
            <i className="ri-list-ordered mr-1"></i>
            ステップ
          </button>
        </div>
      </div>
    </div>
  );
}
